import Assignment from "@mui/icons-material/Assignment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import WhatsApp from "@mui/icons-material/WhatsApp";
import red from "@mui/material/colors/red";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LibTextField, {
  TextFieldProps as LibTextFieldProps,
} from "@mui/material/TextField";
import { styled } from "@mui/system";
import { isEmpty } from "@sd/helpers/String";
import { ChangeEvent, FC, FocusEvent, ReactNode, Ref, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import type { FormInputProps } from "@sd/helpers/Form";

type PureTextFieldProps = {
  label: string;
  onChange: (val: ChangeValue) => void;
  value: string;

  type?: AppInputType;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  autoComplete?: string;
  noMesssage?: boolean;
  noForceNumber?: boolean;
  noDecimalPoint?: boolean;
  rows?: number;
  message?: string;
  error?: boolean;

  inputRef?: Ref<any>;
  endIcon?: ReactNode;

  onBlur?: (e: FocusEvent) => void;
  onIconClick?: () => void;
};

type TextFieldProps = Omit<PureTextFieldProps, "onChange" | "value"> &
  FormInputProps;

type AppInputType =
  | "text"
  | "number"
  | "password"
  | "whatsapp"
  | "copy"
  | "tel";

type ChangeValue = string | number;

const AppTextField = styled(LibTextField)<LibTextFieldProps>(({ error }) => ({
  "input, textarea": {
    color: error ? red[400] : "",
  },
}));

const UPDATED_TYPE: { [index: string]: string } = {
  whatsapp: "tel",
};

const TextField: FC<TextFieldProps> = ({
  name,
  rules,
  defaultValue,
  ...props
}) => {
  const { control } = useFormContext();

  const {
    field: { ref: inputRef, onChange, value },
    fieldState: { error: { message } = {}, invalid: error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const inputProps = {
    ...props,
    inputRef,
    onChange,
    value,
    message,
    error,
  };

  return <PureTextField {...inputProps} />;
};

export const PureTextField: FC<PureTextFieldProps> = ({
  label,
  type: oType = "text",
  disabled,
  readOnly,
  maxLength,
  autoComplete,
  noMesssage,
  noForceNumber,
  noDecimalPoint,
  endIcon,

  onBlur,
  onIconClick,

  inputRef,
  onChange,
  value,
  error,
  message,

  rows = 1,

  ...rest
}) => {
  const pType = UPDATED_TYPE[oType] ?? oType;
  const [type, setType] = useState(pType);
  const [showPassword, setShowPassword] = useState(false);

  const props = {
    ...rest,
    label,
    type,
    inputRef,
    onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      onChange(e.target.value),
    onBlur: onInputBlur,
    value: `${isEmpty(value) ? "" : value}`,
    error,
    helperText: !noMesssage && message,
    disabled,
    variant: "outlined" as const,
    margin: "normal" as const,
    fullWidth: true,
    multiline: rows > 1,
    rows,
    onInput: (e: FocusEvent<HTMLInputElement>) => {
      if (!maxLength) {
        return;
      }

      e.target.value = `${e.target.value}`.slice(0, maxLength);
    },
  };

  const InputProps = {
    readOnly,
    endAdornment: endIcon,
  };

  const inputProps = { maxLength, autoComplete };

  const pwdProps = {
    ...InputProps,
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => {
            onIconClick && onIconClick();
            setShowPassword((s) => !s);
            setType(showPassword ? "password" : "text");
          }}
        >
          {showPassword ? <Visibility /> : <VisibilityOff />}
        </IconButton>
      </InputAdornment>
    ),
  };

  const waProps = {
    ...InputProps,
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => {
            onIconClick && onIconClick();
          }}
        >
          <WhatsApp htmlColor="#128c7e" />
        </IconButton>
      </InputAdornment>
    ),
  };

  const copyProps = {
    ...InputProps,
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => {
            onIconClick && onIconClick();
          }}
        >
          <Assignment />
        </IconButton>
      </InputAdornment>
    ),
  };

  const mutliFieldsProps: { [index: string]: LibTextFieldProps } = {
    password: { ...props, inputProps, InputProps: pwdProps },
    whatsapp: { ...props, inputProps, InputProps: waProps },
    copy: { ...props, inputProps, InputProps: copyProps },
  };

  const textFieldProps = mutliFieldsProps[oType] ?? {
    ...props,
    inputProps,
    InputProps,
  };

  function onInputBlur(e: FocusEvent) {
    if ((pType !== "tel" && pType !== "number") || noForceNumber) {
      onBlur && onBlur(e);
      return;
    }

    if (noDecimalPoint) {
      return forceInteger(e);
    }

    const regEx = /^-?\d+(\.\d{0,2})?$/;
    const valid = new RegExp(regEx).test(value);

    const updated = valid ? +value : "";
    onChange(updated);
    onBlur && onBlur(e);
  }

  function forceInteger(e: FocusEvent) {
    const valid = Number.parseInt(`${value}`);
    const updated = isNaN(valid) ? "" : valid;
    onChange(updated);
    onBlur && onBlur(e);
  }

  return <AppTextField {...textFieldProps} />;
};

export default TextField;
